import { EventMention, MentionsV2, PersonMention } from "@clockwise/schema";
import { useReadCalendarColors } from "@clockwise/web-commons/src/util/CalendarColorsContext";
import { Tooltip } from "@material-ui/core";
import classNames from "classnames";
import { cloneDeep, compact, difference, isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useBoolean } from "usehooks-ts";
import { addCalendarIds, removeCalendarIds } from "../../../state/actions/multi-calendar.actions";
import { EventCardAttendee } from "../../event-card/types";
import {
  useReadSuggestedPrompt,
  useUpdateSuggestedPrompt,
} from "../ai-chat/hooks/SuggestedPromptContext";
import { AI_EXCLUSION_TOOLTIP } from "../constants";
import { useMentionsSearch } from "../hooks/useMentionsSearch";
import {
  addStyledEventMentionToInput,
  addStyledPersonMentionToInput,
  fetchPotentialMention,
  getNewMentionIndex,
  putCaretAtEndOfInput,
} from "../util/chatInput.util";
import "./ChatBox.css";
import { ChatInputLoading } from "./ChatInputLoading";
import { ChatMentionAutoComplete } from "./ChatMentionAutoComplete";
import { ProcessMessageButton } from "./ProcessMessageButton";

type Mentions = {
  personMentions: PersonMention[];
  eventMentions: EventMention[];
};
const emptyMentions: Mentions = {
  personMentions: [],
  eventMentions: [],
};
export const ChatUserInput = ({
  submitMessage,
  loading,
  focusInput,
  disabled,
  cancelMessage,
}: {
  submitMessage: (innerText: string, mentions: MentionsV2) => Promise<void>;
  loading: boolean;
  focusInput: boolean;
  disabled: boolean;
  cancelMessage: () => void;
}) => {
  const {
    searchPersons,
    handleMentionSearch,
    resetSearch,
    setShowEmptySearchState,
    searchEvents,
  } = useMentionsSearch();

  const dispatch = useDispatch();
  const [mentionsToSend, setMentionsToSend] = useState<Mentions>(emptyMentions);
  const [indexSelectedMention, setIndexSelectedMention] = useState<number>(0);
  const [mentionSearchQuery, setMentionSearchQuery] = useState<string>("");
  const { value: hasFocus, setTrue: gainFocus, setFalse: removeFocus } = useBoolean(true);

  const chatInputRef = useRef<HTMLDivElement>(null);
  const suggestedPromptText = useReadSuggestedPrompt();
  const updateSuggestedPromptText = useUpdateSuggestedPrompt();

  useEffect(() => {
    // Auto focus chat input on mount
    if (!!chatInputRef.current && focusInput) {
      chatInputRef.current.focus();
    }
  }, [focusInput]);

  useEffect(() => {
    if (!!suggestedPromptText && !!chatInputRef.current) {
      chatInputRef.current.innerText = suggestedPromptText;

      putCaretAtEndOfInput(chatInputRef.current);

      maybeHandleSearch(suggestedPromptText, chatInputRef.current.children);

      updateSuggestedPromptText(null);
    }
  }, [suggestedPromptText]);

  // We add this check to prevent a tiny split second of input wanting to add new line right before sending the message
  // Same goes to arrow keys when selecting a mention
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && loading) {
      cancelMessage();
    }
    if (e.key === "Enter" && !e.shiftKey && chatInputRef.current && !searchPersons.length) {
      e.preventDefault();
    }
    if (searchPersons.length) {
      if (e.key === "Enter" || e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
      }
    }
  };

  // Instead of recording the mentions that were removed, we check for mentions that were removed right before sending the message
  const getFinalDraftOfAddedMentions = (): Mentions => {
    const textInMessage = chatInputRef.current?.textContent;

    let eventMentions: EventMention[] = [];
    for (const eventMention of mentionsToSend.eventMentions) {
      if (textInMessage?.includes(eventMention.title)) {
        eventMentions.push(eventMention);
      }
    }
    let personMentions: PersonMention[] = [];
    for (const personMention of mentionsToSend.personMentions) {
      if (textInMessage?.includes(personMention.name)) {
        personMentions.push(personMention);
      }
    }

    return {
      eventMentions,
      personMentions,
    };
  };

  const validateInputLength = (inputString: string | null) => {
    if (!inputString) {
      return false;
    }
    return inputString.trim().length > 0;
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (searchPersons.length || searchEvents.length) {
      //If the user is actively selecting a mention, we want them to be able to browse and choose with the keyboard.
      if (e.key === "Enter") {
        if (indexSelectedMention < searchPersons.length) {
          const personMentionToAdd: EventCardAttendee = searchPersons[indexSelectedMention];
          onAdd(undefined, personMentionToAdd);
        } else {
          const eventMentionToAdd: EventMention =
            searchEvents[indexSelectedMention - searchPersons.length];
          onAdd(eventMentionToAdd, undefined);
        }
      } else {
        setIndexSelectedMention(
          getNewMentionIndex(
            e.key,
            indexSelectedMention,
            searchPersons.length,
            searchEvents.length,
          ),
        );
      }
      //Case: User hits enter and there is no mention in progress
    } else if (e.key === "Enter" && !e.shiftKey && chatInputRef.current && !loading) {
      const inputNotEmpty = validateInputLength(chatInputRef.current.innerText);
      if (inputNotEmpty) {
        const finalMentions = getFinalDraftOfAddedMentions();

        void submitMessage(chatInputRef.current.innerText || "", finalMentions);
        chatInputRef.current.innerText = "";
        setMentionsToSend(emptyMentions);
        return;
      }
    }
    // Add the character they've typed and handle mention search
    maybeHandleSearch(chatInputRef?.current?.innerText, chatInputRef?.current?.children);
  };

  const onClickSubmit = () => {
    if (chatInputRef.current && !loading) {
      const inputNotEmpty = validateInputLength(chatInputRef.current.innerText);
      if (inputNotEmpty) {
        const finalMentions = getFinalDraftOfAddedMentions();

        void submitMessage(chatInputRef.current.innerText || "", finalMentions);
        chatInputRef.current.innerText = "";
        setMentionsToSend(emptyMentions);
        return;
      }
    }
  };

  const onAdd = (eventSearchValue?: EventMention, personSearchValue?: EventCardAttendee) => {
    if (eventSearchValue) {
      onAddEventMention(eventSearchValue);
    } else if (personSearchValue) {
      onAddPersonMention(personSearchValue);
    }
  };

  const onAddEventMention = useCallback(
    (newEvent: EventMention) => {
      if (newEvent.title && chatInputRef.current) {
        addStyledEventMentionToInput(chatInputRef.current, newEvent, mentionSearchQuery);
        const copyOfMentions = cloneDeep(mentionsToSend);
        const newEventMentions = copyOfMentions.eventMentions;
        newEventMentions.push(newEvent);
        const newMentions = {
          ...mentionsToSend,
          eventMentions: newEventMentions,
        };
        clearSearch();
        setMentionsToSend(newMentions);
        setTimeout(() => {
          setIndexSelectedMention(0);
          putCaretAtEndOfInput(chatInputRef.current);
        }, 0);
      }
    },
    [mentionSearchQuery, mentionsToSend, resetSearch],
  );

  const calendarColorMap = useReadCalendarColors();
  const onAddPersonMention = useCallback(
    (newPerson: EventCardAttendee) => {
      if (newPerson.profile && chatInputRef.current) {
        const mention = addStyledPersonMentionToInput(
          chatInputRef.current,
          newPerson,
          mentionSearchQuery,
        );
        const copyOfMentions = cloneDeep(mentionsToSend);
        const newPersonMentions = copyOfMentions.personMentions;
        newPersonMentions.push(mention);
        const newMentions = {
          ...mentionsToSend,
          personMentions: newPersonMentions,
        };
        clearSearch();
        setMentionsToSend(newMentions);
        dispatch(addCalendarIds([newPerson.primaryCalendar]));

        setTimeout(() => {
          setIndexSelectedMention(0);
          putCaretAtEndOfInput(chatInputRef.current);
        }, 0);
      }
    },
    [mentionSearchQuery, mentionsToSend, resetSearch, calendarColorMap],
  );

  useEffect(() => {
    // Update the `chatInputRef.current` to have the correct mentions colors
    if (chatInputRef.current) {
      const chatInputChildren = Array.from(chatInputRef.current.children);
      for (const child of chatInputChildren) {
        const primaryCalendarB64 = child.getAttribute("data-primary-calendar-b64");
        if (primaryCalendarB64) {
          const primaryCalendar = atob(primaryCalendarB64);
          const color = calendarColorMap[primaryCalendar]?.border || "black";
          child.setAttribute("style", `color: ${color}`);
        }
      }
    }
  }, [calendarColorMap, mentionsToSend]);

  const maybeRemoveAtMentions = useCallback(
    (chatInputChildren?: HTMLCollection) => {
      const calendarIdsInMentions: string[] = mentionsToSend.personMentions.map(
        (mention) => mention.calendarId,
      );
      const calendarIdsInChatInput: string[] = compact(
        Array.prototype.slice.call(chatInputChildren).map((element: HTMLElement) => {
          const primaryCalendarB64 = element.getAttribute("data-primary-calendar-b64");

          return primaryCalendarB64 ? atob(primaryCalendarB64) : null;
        }),
      );
      const calendarIdsToRemove = difference(calendarIdsInMentions, calendarIdsInChatInput);

      if (isEmpty(calendarIdsToRemove)) {
        return;
      }

      dispatch(removeCalendarIds(calendarIdsToRemove));

      const newPersonMentions = mentionsToSend.personMentions.filter((mention) => {
        return !calendarIdsToRemove.includes(mention.calendarId);
      });

      setMentionsToSend({
        ...mentionsToSend,
        personMentions: [...newPersonMentions],
      });
    },
    [mentionsToSend],
  );

  const maybeHandleSearch = useCallback(
    (promptInput?: string, chatInputChildren?: HTMLCollection) => {
      const { isAMention, wordToSearch, isEmptyAtSymbol } = fetchPotentialMention(promptInput);
      if (isEmptyAtSymbol) {
        setShowEmptySearchState(true);
      } else if (isAMention) {
        handleMentionSearch(wordToSearch);
        setMentionSearchQuery(wordToSearch);
      } else {
        clearSearch();
      }

      maybeRemoveAtMentions(chatInputChildren);
    },
    [handleMentionSearch, resetSearch, setShowEmptySearchState],
  );

  const hasMentions = useMemo(() => {
    return !isEmpty(searchPersons) || !isEmpty(searchEvents);
  }, [searchPersons, searchEvents]);

  const updateHighlightedSuggestion = useCallback((index: number) => {
    setIndexSelectedMention(index);
  }, []);

  const clearSearch = () => {
    setMentionSearchQuery("");
    resetSearch();
  };

  const handleProcessMessageButtonClick = () => {
    if (disabled) return;
    if (loading) {
      cancelMessage();
    } else {
      onClickSubmit();
    }
  };

  const hasText =
    (chatInputRef.current && chatInputRef.current?.innerText.trim()?.length > 0) || false;

  return (
    <>
      <div className="cw-relative">
        <ChatMentionAutoComplete
          isOpen={hasFocus && hasMentions}
          personSuggestions={searchPersons}
          eventSuggestions={searchEvents}
          onAddSuggestion={onAdd}
          highlightedSuggestion={indexSelectedMention}
          setHighlightedSuggestion={updateHighlightedSuggestion}
          referenceElement={chatInputRef}
        />
      </div>
      <Tooltip title={disabled ? AI_EXCLUSION_TOOLTIP : ""}>
        <div className="cw-flex cw-flex-col cw-relative">
          <div
            ref={chatInputRef}
            data-placeholder={loading ? null : "Schedule or reschedule..."}
            aria-label={loading ? "Working on it..." : "Schedule or reschedule..."}
            aria-disabled={disabled || loading}
            dir="auto"
            suppressContentEditableWarning
            onFocus={gainFocus}
            onBlur={removeFocus}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            className={classNames(
              "cw-py-2 cw-pr-8 cw-pl-[14px]",
              "cw-border cw-border-solid cw-outline-1 cw-body-sm cw-text-13",
              "cw-bg-neutral cw-text-default cw-rounded-xl",
              {
                loading: "loading",
                "cw-cursor-not-allowed cw-border-default-disabled": disabled,
                "focus-visible:cw-outline-brand-interactable focus:cw-outline-brand-interactable focus:hover:cw-outline-brand-interactable-hover": !disabled,
              },
            )}
            role="textbox"
            aria-multiline="true"
            contentEditable={!disabled && !loading}
            spellCheck={false}
            cw-id="chat-input"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData("text");
              document.execCommand("insertText", false, text);
            }}
          >
            {loading && <ChatInputLoading />}
          </div>
          <div className="cw-flex cw-items-end cw-h-5 cw-absolute cw-right-2 cw-top-[9.5px]">
            <ProcessMessageButton
              disabled={disabled || (!loading && !hasText)}
              onClick={handleProcessMessageButtonClick}
              processing={loading}
            />
          </div>
        </div>
      </Tooltip>
    </>
  );
};
